import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, Check, Mic, Calculator, Brain, Code2, Keyboard, Stethoscope, PhoneCall, Boxes, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const paths = [
  { id: "AMCAT", label: "AMCAT", desc: "Aptitude, reasoning & technical employability test.", icon: Calculator },
  { id: "Versant", label: "Versant Voice & Accent", desc: "Spoken English, fluency and pronunciation.", icon: Mic },
  { id: "Aptitude", label: "Aptitude", desc: "Quantitative and logical problem solving.", icon: Brain },
  { id: "Technical", label: "Technical", desc: "Programming, DSA and CS fundamentals.", icon: Code2 },
  { id: "Typing", label: "Typing Speed", desc: "Improve your WPM and accuracy.", icon: Keyboard },
  { id: "Medical Billing", label: "Medical Billing", desc: "US healthcare revenue cycle, coding & claims.", icon: Stethoscope },
  { id: "AR Calling", label: "AR Calling", desc: "Payer follow-up, denials & appeals for RCM.", icon: PhoneCall },
  { id: "SAP MM", label: "SAP MM", desc: "SAP Materials Management: procurement, inventory & P2P.", icon: Boxes },
  { id: "Financial Crime", label: "Financial Crime & AML", desc: "AML, KYC, CDD, EDD, sanctions, monitoring & SAR.", icon: ShieldCheck },
];

export default function Onboarding() {
  const { user, updateUser, loading } = useAuth();
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/auth");
    if (user?.role === "admin") navigate("/admin");
    if (user?.target_path) navigate("/dashboard");
  }, [user, loading, navigate]);

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const { data } = await api.put("/users/me/path", { target_path: selected });
      updateUser(data.user);
      toast.success("Preparation path set!");
      navigate("/dashboard");
    } catch {
      toast.error("Could not save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-6 noise-overlay">
      <div className="w-full max-w-3xl">
        <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-3">Onboarding</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter mb-2">Choose your target path</h1>
        <p className="text-muted-foreground mb-8">Pick the assessment you're preparing for. You can switch anytime.</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
          {paths.map((p) => {
            const Icon = p.icon;
            const active = selected === p.id;
            return (
              <button
                key={p.id}
                onClick={() => setSelected(p.id)}
                data-testid={`path-${p.id}`}
                className={`text-left p-5 rounded-md border transition-colors ${
                  active ? "border-primary bg-primary/5" : "border-border bg-card hover:bg-secondary"
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center mb-3">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  {active && <Check className="w-5 h-5 text-primary" />}
                </div>
                <h3 className="font-heading font-bold text-lg">{p.label}</h3>
                <p className="text-sm text-muted-foreground mt-1">{p.desc}</p>
              </button>
            );
          })}
        </div>

        <Button onClick={save} disabled={!selected || saving} className="rounded-md h-11 px-8" data-testid="onboarding-continue">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Continue to Dashboard"}
        </Button>
      </div>
    </div>
  );
}
